"use client";

import { ArrowLeft, BookOpen, Zap, Palette, Layers, Shapes } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslations } from "@/lib/use-translations";
import type { WordModule } from "@/lib/word-modules";

interface ModuleSelectorProps {
  modules: WordModule[];
  onSelect: (module: WordModule) => void;
  onBack: () => void;
  knownCount?: number;
}

const MODULE_STYLES: Record<WordModule["icon"], { Icon: typeof BookOpen; color: string; bg: string; border: string }> = {
  noun: { Icon: BookOpen, color: "text-blue-500", bg: "bg-blue-500/10", border: "border-blue-500/20" },
  verb: { Icon: Zap, color: "text-orange-500", bg: "bg-orange-500/10", border: "border-orange-500/20" },
  descriptor: { Icon: Palette, color: "text-purple-500", bg: "bg-purple-500/10", border: "border-purple-500/20" },
  other: { Icon: Shapes, color: "text-slate-500", bg: "bg-slate-500/10", border: "border-slate-500/20" },
  all: { Icon: Layers, color: "text-emerald-500", bg: "bg-emerald-500/10", border: "border-emerald-500/20" },
};

export function ModuleSelector({ modules, onSelect, onBack, knownCount }: ModuleSelectorProps) {
  const t = useTranslations();

  return (
    <div className="p-4 space-y-4 max-w-lg mx-auto">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack} className="rounded-xl">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            {t.learning.chooseModule || "Choose a module"}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t.learning.moduleHint || "Practice in smaller groups for better retention"}
          </p>
        </div>
      </div>

      {knownCount != null && knownCount > 0 && (
        <p className="text-xs text-muted-foreground text-center">
          {(t.learning.knownWordsHidden || "{count} known words hidden").replace("{count}", String(knownCount))}
        </p>
      )}

      <div className="space-y-3">
        {modules.map((mod) => {
          const style = MODULE_STYLES[mod.icon];
          const Icon = style.Icon;

          return (
            <button
              key={mod.id}
              onClick={() => onSelect(mod)}
              className={`
                w-full flex items-center gap-4 p-4 rounded-xl border transition-all
                ${style.border} hover:shadow-md hover:scale-[1.01] active:scale-[0.99] cursor-pointer
                bg-card
              `}
            >
              <div className={`w-12 h-12 rounded-lg ${style.bg} flex items-center justify-center shrink-0`}>
                <Icon className={`w-6 h-6 ${style.color}`} />
              </div>
              <div className="text-left">
                <div className="font-medium text-foreground">{mod.label}</div>
                <div className="text-sm text-muted-foreground">
                  {mod.words.length} {t.learning.wordsCount || "words"}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
